// SolarInstallationIntercomService.js - Detailed Intercom & PABX Systems service component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a detailed Intercom & PABX Systems service section
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the service section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The Intercom service section elements
 */
export const createSolarInstallationIntercomServiceElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const intercomSectionClass = 'intercom-service-section';
  const intercomContainerClass = 'intercom-service-container';
  const intercomHeaderClass = 'intercom-service-header';
  const intercomTitleClass = 'intercom-service-title';
  const intercomSubtitleClass = 'intercom-service-subtitle';
  const intercomContentClass = 'intercom-service-content';
  const intercomHeroClass = 'intercom-service-hero';
  const intercomHeroImageClass = 'intercom-service-hero-image';
  const intercomHeroTextClass = 'intercom-service-hero-text';
  const intercomFeaturesClass = 'intercom-service-features';
  const intercomFeatureCardClass = 'intercom-service-feature-card';
  const intercomFeatureIconClass = 'intercom-service-feature-icon';
  const intercomFeatureTitleClass = 'intercom-service-feature-title';
  const intercomFeatureDescClass = 'intercom-service-feature-desc';
  const intercomPackagesClass = 'intercom-service-packages';
  const intercomPackageCardClass = 'intercom-service-package-card';
  const intercomPackageTitleClass = 'intercom-service-package-title';
  const intercomPackagePriceClass = 'intercom-service-package-price';
  const intercomPackageListClass = 'intercom-service-package-list';
  const intercomPackageItemClass = 'intercom-service-package-item';
  const intercomCtaClass = 'intercom-service-cta';
  const intercomCtaButtonClass = 'intercom-service-cta-button';
  
  // Desktop Styles - Solar themed with deep blue background
  const intercomSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#0f2341',
    background: `
      radial-gradient(circle at 20% 80%, rgba(74, 222, 128, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 197, 94, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #0f2341 0%, #1e3a8a 50%, #0f2341 100%)
    `,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const intercomContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '60px'
  };

  const intercomHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%'
  };

  const intercomTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const intercomSubtitleStyles = {
    fontSize: '20px',
    color: '#e2e8f0',
    lineHeight: '1.6',
    marginBottom: '30px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400',
    maxWidth: '700px'
  };

  const intercomContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '80px'
  };

  const intercomHeroStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '50px',
    width: '100%'
  };

  const intercomHeroImageStyles = {
    flex: '1',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
  };

  const intercomHeroTextStyles = {
    flex: '1',
    color: '#ffffff',
    fontSize: '18px',
    lineHeight: '1.8',
    fontFamily: '"Inter", sans-serif'
  };

  const intercomFeaturesStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '30px',
    width: '100%'
  };

  const intercomFeatureCardStyles = {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
    padding: '30px',
    border: '1px solid rgba(74, 222, 128, 0.2)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease'
  };

  const intercomFeatureIconStyles = {
    fontSize: '48px',
    marginBottom: '20px',
    display: 'block'
  };

  const intercomFeatureTitleStyles = {
    fontSize: '24px',
    fontWeight: '600',
    color: '#4ade80',
    marginBottom: '15px',
    fontFamily: '"Inter", sans-serif'
  };

  const intercomFeatureDescStyles = {
    fontSize: '16px',
    color: '#e2e8f0',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif'
  };

  const intercomPackagesStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px',
    width: '100%'
  };

  const intercomPackageCardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '40px',
    border: '2px solid #4ade80',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease'
  };

  const intercomPackageTitleStyles = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#0f2341',
    marginBottom: '10px',
    fontFamily: '"Inter", sans-serif'
  };

  const intercomPackagePriceStyles = {
    fontSize: '36px',
    fontWeight: '700',
    color: '#4ade80',
    marginBottom: '25px',
    fontFamily: '"Inter", sans-serif'
  };

  const intercomPackageListStyles = {
    listStyle: 'none',
    padding: '0',
    margin: '0'
  };

  const intercomPackageItemStyles = {
    fontSize: '16px',
    color: '#475569',
    marginBottom: '12px',
    paddingLeft: '25px',
    position: 'relative',
    fontFamily: '"Inter", sans-serif'
  };

  const intercomCtaStyles = {
    textAlign: 'center',
    width: '100%'
  };

  const intercomCtaButtonStyles = {
    padding: '18px 40px',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: '#4ade80',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: '"Inter", sans-serif',
    boxShadow: '0 4px 15px rgba(74, 222, 128, 0.3)'
  };

  // Mobile styles
  const mobileIntercomHeroStyles = {
    flexDirection: 'column',
    gap: '30px'
  };

  const mobileIntercomTitleStyles = {
    fontSize: '36px'
  };

  const mobileIntercomPackageCardStyles = {
    padding: '30px 20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(intercomSectionClass, intercomSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomContainerClass, intercomContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomHeaderClass, intercomHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomTitleClass, intercomTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomSubtitleClass, intercomSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomContentClass, intercomContentStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomHeroClass, intercomHeroStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomHeroImageClass, intercomHeroImageStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomHeroTextClass, intercomHeroTextStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomFeaturesClass, intercomFeaturesStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomFeatureCardClass, intercomFeatureCardStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomFeatureIconClass, intercomFeatureIconStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomFeatureTitleClass, intercomFeatureTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomFeatureDescClass, intercomFeatureDescStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomPackagesClass, intercomPackagesStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomPackageCardClass, intercomPackageCardStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomPackageTitleClass, intercomPackageTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomPackagePriceClass, intercomPackagePriceStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomPackageListClass, intercomPackageListStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomPackageItemClass, intercomPackageItemStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomCtaClass, intercomCtaStyles);
    newRegistry[DEVICES.DESKTOP].set(intercomCtaButtonClass, intercomCtaButtonStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(intercomHeroClass, mobileIntercomHeroStyles);
    newRegistry[DEVICES.MOBILE].set(intercomTitleClass, mobileIntercomTitleStyles);
    newRegistry[DEVICES.MOBILE].set(intercomPackageCardClass, mobileIntercomPackageCardStyles);

    return newRegistry;
  });
  
  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    intercomSectionClass,
    intercomContainerClass,
    intercomHeaderClass,
    intercomTitleClass,
    intercomSubtitleClass,
    intercomContentClass,
    intercomHeroClass,
    intercomHeroImageClass,
    intercomHeroTextClass,
    intercomFeaturesClass,
    intercomFeatureCardClass,
    intercomFeatureIconClass,
    intercomFeatureTitleClass,
    intercomFeatureDescClass,
    intercomPackagesClass,
    intercomPackageCardClass,
    intercomPackageTitleClass,
    intercomPackagePriceClass,
    intercomPackageListClass,
    intercomPackageItemClass,
    intercomCtaClass,
    intercomCtaButtonClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  const contentId = sectionId + 5;
  const heroId = sectionId + 6;
  const heroImageId = sectionId + 7;
  const heroTextId = sectionId + 8;
  const featuresId = sectionId + 9;
  const packagesId = sectionId + 20;
  const ctaId = sectionId + 30;
  const ctaButtonId = sectionId + 31;

  const newElements = [
    // Main Intercom Service Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Intercom Service Section',
      parentId: null,
      className: intercomSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'intercom-service-section',
        'data-component': 'section',
        'data-section-type': 'service',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Intercom Service Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: intercomContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'intercom-service-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Intercom Service Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: intercomHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'intercom-service-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Intercom Service Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h1'),
      className: intercomTitleClass,
      styles: {},
      children: [],
      text: 'Intercoms & PABX Systems',
      attributes: {
        'data-element': 'intercom-service-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Intercom Service Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: intercomSubtitleClass,
      styles: {},
      children: [],
      text: 'Professional communication infrastructure solutions for seamless connectivity. Complete intercom systems, PABX installations, and advanced telecommunication networks for businesses and residential complexes.',
      attributes: {
        'data-element': 'intercom-service-subtitle'
      }
    },
    
    // Content Container
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Intercom Service Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: intercomContentClass,
      styles: {},
      children: [heroId, featuresId, packagesId, ctaId],
      text: '',
      attributes: {
        'data-element': 'intercom-service-content'
      }
    },
    
    // Hero Section
    {
      id: heroId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Intercom Service Hero',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: intercomHeroClass,
      styles: {},
      children: [heroImageId, heroTextId],
      text: '',
      attributes: {
        'data-element': 'intercom-service-hero'
      }
    },
    
    // Hero Image
    {
      id: heroImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Intercom Service Hero Image',
      parentId: heroId,
      hierarchyName: getHierarchyName('image'),
      className: intercomHeroImageClass,
      styles: { width: '100%', height: 'auto' },
      src: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      children: [],
      text: '',
      attributes: {
        'data-element': 'intercom-service-hero-image',
        'alt': 'Professional intercom and PABX system installation'
      }
    },
    
    // Hero Text
    {
      id: heroTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Intercom Service Hero Text',
      parentId: heroId,
      hierarchyName: getHierarchyName('div'),
      className: intercomHeroTextClass,
      styles: {},
      children: [],
      text: 'JNT & Co. delivers comprehensive communication infrastructure solutions that connect your entire facility seamlessly. Our intercom and PABX systems provide reliable internal communication, visitor management, and telephone networking for residential complexes, commercial buildings, and industrial facilities. We specialize in IP-based intercom systems, digital PABX installations, video door phones, and integrated communication networks. Our solutions enhance security, improve operational efficiency, and provide crystal-clear communication throughout your property.',
      attributes: {
        'data-element': 'intercom-service-hero-text'
      }
    }
  ];

  return {
    intercomServiceSection: newElements[0],
    elements: newElements
  };
}; 